import { detect, fromNavigator, fromStorage } from '@lingui/detect-locale';
import { useIsFetching } from '@tanstack/react-query';
import { BackendClient } from '@tf/api';
import { useGetAuthToken } from '@tf/shared';
import { Loader, Select } from '@tf/ui';
import { decodeJwt } from 'jose';
import type React from 'react';
import { useEffect } from 'react';

import { useDefinitions } from '@/core/providers';
import { loadCatalog, useCurrentLocale } from '@/core/providers/I18nProvider';

const localStorageKey = 'client_area-locale';

export const UserLocaleSetter: React.FC = () => {
	const currentLocale = useCurrentLocale();

	useEffect(() => {
		BackendClient.getInstance().setLocale({ locale: currentLocale });
	}, [currentLocale]);

	return null;
};

export const LocaleSwitcher: React.FC = () => {
	const { defs } = useDefinitions();
	const currentLocale = useCurrentLocale();
	const getToken = useGetAuthToken();

	const isFetchingDefs = useIsFetching({ queryKey: ['definitions'] });

	// restore user preferred locale
	useEffect(() => {
		const getPreferredLocale = async () => {
			const availableLocales = defs.locales.map((locale) => locale.value);

			const token = await getToken();
			if (token) {
				const claims = decodeJwt(token);
				if ('locale' in claims && typeof claims.locale === 'string' && availableLocales.includes(claims.locale)) {
					return claims.locale;
				}
			}

			const detectedLocale = detect(fromStorage(localStorageKey), fromNavigator(), currentLocale);
			if (detectedLocale && availableLocales.includes(detectedLocale)) {
				return detectedLocale;
			}

			return currentLocale;
		};

		getPreferredLocale().then(loadCatalog);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getToken, defs.locales.map, currentLocale]);

	if (defs.locales.length < 2) {
		return null;
	}

	return (
		<Select
			value={currentLocale}
			data={defs.locales}
			rightSection={isFetchingDefs ? <Loader size={14} /> : undefined}
			onChange={(newLocale) => {
				if (newLocale) {
					loadCatalog(newLocale);
					localStorage.setItem(localStorageKey, newLocale);
					BackendClient.getInstance().setLocale({ locale: newLocale });
				}
			}}
		/>
	);
};
